import React, { useEffect, useRef, useState } from "react";
import { collectYears, drawYearTrail, waitForFonts, MIN_POINTS, TRAIL_SIZES } from "../utils/renderYearTrail";
import exportCanvas from "../utils/exportImage";
import "./YearTrailModal.css";

/**
 * 한 해의 궤적을 이미지로 내보낸다.
 *
 * 미리보기를 먼저 보여준다. 누르자마자 파일이 떨어지면 무엇을
 * 내보내는지 모른 채 공유하게 된다.
 */
export default function YearTrailModal({ books, uid, name, year, onClose }) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const closeRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | working | manual
  const [msg, setMsg] = useState("");
  /* 규격. 4:5 는 인스타 게시물, 9:16 은 스토리다. */
  const [size, setSize] = useState("feed");

  const data = collectYears(books, uid).find((y) => y.year === year);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);

    // 배경 스크롤은 잠그지 않는다. body 를 건드리는 방식은 iOS 에서
    // 화면이 튀어 모달이 안 보이게 만들었다. 겹칩이 화면을 덮으므로
    // 뒤가 조금 움직여도 문제가 되지 않는다.
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!data || !canvasRef.current) return;
    let alive = true;

    // 먼저 한 번 그린다. 폰트를 기다린 뒤에야 그리면 그 사이 캔버스가
    // 빈 채로 남아, 연도를 눌러야 나타나는 것처럼 보였다.
    drawYearTrail(canvasRef.current, { ...data, name, size });

    // 폰트가 도착하면 다시 그린다. 첫 그림은 기본 글꼴일 수 있다.
    waitForFonts().then(() => {
      if (alive && canvasRef.current) drawYearTrail(canvasRef.current, { ...data, name, size });
    });

    return () => { alive = false; };
  }, [data, name, size]);

  const handleExport = async () => {
    if (!canvasRef.current) return;
    setState("working");
    setMsg("");
    try {
      const how = await exportCanvas(canvasRef.current, `muroo-${year}-궤적-${TRAIL_SIZES[size].label}.png`);
      if (how === "manual") {
        setState("manual");
        setMsg("아래 그림을 길게 눌러 저장해주세요");
      } else {
        setState("idle");
        setMsg(how === "shared" ? "" : "저장했어요");
      }
    } catch (e) {
      setState("idle");
      setMsg("이미지를 만들지 못했어요");
    }
  };

  return (
    <div className="yt-overlay" ref={overlayRef} onClick={onClose}>
      <div className="yt-modal" role="dialog" aria-modal="true" aria-label="궤적 내보내기" onClick={(e) => e.stopPropagation()}>
        <div className="yt-head">
          <div className="yt-title">{year} 궤적</div>
          <button className="yt-close" onClick={onClose} aria-label="닫기">✕</button>
        </div>

        {!data ? (
          <div className="yt-empty">
            이 해에는 궤적을 그릴 수 없어요.
            <div className="yt-empty-sub">
              감정 좌표를 찍은 완독 책이 {MIN_POINTS}권 이상이어야 선이 생깁니다.
            </div>
          </div>
        ) : (
          <>
            {/* 규격을 그림 위에 둔다. 아래에 두면 내보내기를 누른 뒤에야
                눈에 들어와, 이미 나간 것을 다시 만들게 된다. */}
            <div className="yt-sizes" role="group" aria-label="규격">
              {Object.values(TRAIL_SIZES).map((s) => (
                <button
                  key={s.key}
                  className={`yt-size${size === s.key ? " on" : ""}`}
                  onClick={() => setSize(s.key)}
                  aria-pressed={size === s.key}
                >{s.label}</button>
              ))}
            </div>
            <canvas ref={canvasRef} className={`yt-canvas yt-${size}`} />
            <button className="yt-export" onClick={handleExport} disabled={state === "working"}>
              {state === "working" ? "만드는 중…" : "이미지 내보내기"}
            </button>
            {msg && <div className="yt-msg">{msg}</div>}
          </>
        )}

        <button ref={closeRef} className="yt-done" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}
